import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	FormControl,
	FormErrorMessage,
	FormLabel,
	Input,
	InputGroup,
	InputRightElement,
	Button,
	Text,
} from "@chakra-ui/react";
import {
	getTextSearchPredictions,
	getNewSessionToken,
	getPlaceDetails,
} from "../lib/googlemaps";
import { AddressInput } from "@/__generated__/graphql";
import {
	Command,
	CommandEmpty,
	CommandInput,
	CommandItem,
	CommandList,
} from "./ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import debounce from "lodash.debounce";
import { Button as ShadButton } from "./ui/button";
import { ArrowLeft, MapPinOff } from "lucide-react";

type EntryMode = "readonly" | "search" | "manual";

interface AddressSelectorProps {
	value?: AddressInput;
	onChange: (address: AddressInput | undefined) => void;
	isInvalid?: boolean;
	country?: { label: string; value: string } | undefined;
	onCountryChange?: (country: { label: string; value: string }) => void;
}

export const AddressSelector: React.FC<AddressSelectorProps> = ({
	value,
	onChange,
	isInvalid,
	country,
	onCountryChange,
}) => {
	const [sessionToken, setSessionToken] =
		useState<google.maps.places.AutocompleteSessionToken | null>(null);
	const [entryMode, setEntryMode] = useState<EntryMode>(
		value ? "readonly" : "search"
	);
	const [loading, setLoading] = useState(false);
	const [locationIsNotDescriptive, setLocationIsNotDescriptive] =
		useState(false);
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState("");
	const [predictions, setPredictions] = useState<
		{ label: string; value: string }[]
	>([]);
	const [manualAddressLine, setManualAddressLine] = useState("");
	const [manualCountry, setManualCountry] = useState<
		{ label: string; value: string } | undefined
	>(country);
	const [manualState, setManualState] = useState<string>("");
	const [manualCityOrLga, setManualCityOrLga] = useState<string>("");
	const [lgaOpen, setLgaOpen] = useState(false);

	useEffect(() => {
		setSessionToken(getNewSessionToken());
	}, []);

	const fetchPredictions = useCallback(
		async (inputValue: string) => {
			if (!sessionToken) return;
			if (!inputValue.trim()) {
				setPredictions([]);
				return;
			}
			setLoading(true);
			try {
				const res = await getTextSearchPredictions(inputValue, sessionToken);
				setPredictions(
					res.map((p) => ({ label: p.description, value: p.place_id! }))
				);
			} catch (err) {
				console.error("Error fetching predictions:", err);
			} finally {
				setLoading(false);
			}
		},
		[sessionToken]
	);

	const debouncedFetch = useMemo(
		() =>
			debounce((q: string) => {
				fetchPredictions(q);
			}, 300),
		[fetchPredictions]
	);

	useEffect(() => {
		return () => {
			debouncedFetch.cancel();
		};
	}, [debouncedFetch]);

	const handlePredictionClick = async (placeId: string) => {
		if (!sessionToken) return;

		setLoading(true);

		try {
			const place = await getPlaceDetails(placeId, sessionToken);

			const state = place.address_components!.find((component) =>
				component.types.includes("administrative_area_level_1")
			);

			const city = place.address_components!.find((component) =>
				component.types.includes("administrative_area_level_2")
			);

			if (!place.formatted_address) {
				setLocationIsNotDescriptive(true);
				setLoading(false);
				return;
			} else {
				setLocationIsNotDescriptive(false);
			}

			const result: AddressInput = {
				address: place.name || place.formatted_address,
				formattedAddress: place.formatted_address,
				state: state?.long_name,
				city: city?.long_name,
				placeId: place.place_id!,
			};

			onChange(result);
			setEntryMode("readonly");
		} catch (error) {
			console.error("Error getting place details:", error);
		} finally {
			setLoading(false);
		}
	};

	// Country/state/LGA helpers for manual entry
	const nigeriaLgaData = ((): { name: string; lgas: string[] }[] => {
		try {
			// eslint-disable-next-line @typescript-eslint/no-var-requires
			const sl = require("../assets/state-lgas.json");
			const ng = sl?.countries?.find((c: any) => c.name === "Nigeria");
			return ng?.states || [];
		} catch (e) {
			return [];
		}
	})();

	const countriesList = useMemo(() => {
		try {
			// eslint-disable-next-line @typescript-eslint/no-var-requires
			const c = require("../assets/countries.json");
			return Object.values(c).map((co: any) => ({
				label: co.name as string,
				value: co.alpha2 as string,
			}));
		} catch (e) {
			return [] as { label: string; value: string }[];
		}
	}, []);

	const selectedCountry = manualCountry || country;
	const useNigeriaFlow =
		selectedCountry?.value === "NG" && nigeriaLgaData.length > 0;

	const allLgas = useMemo(() => {
		if (!useNigeriaFlow) return [] as { lga: string; state: string }[];
		const rows: { lga: string; state: string }[] = [];
		for (const st of nigeriaLgaData) {
			for (const l of st.lgas) {
				rows.push({ lga: l, state: st.name });
			}
		}
		return rows;
	}, [nigeriaLgaData, useNigeriaFlow]);

	const saveManualAddress = () => {
		const co = selectedCountry;
		if (!co) return;
		const state = manualState;
		const cityOrLga = manualCityOrLga;
		const addressLine = manualAddressLine.trim();
		const countryLabel =
			countriesList.find((x) => x.value === co.value)?.label || co.label;

		const formatted = [addressLine, cityOrLga, state, countryLabel]
			.filter(Boolean)
			.join(", ");

		const result: AddressInput = {
			address: addressLine || formatted,
			formattedAddress: formatted,
			state: state || undefined,
			city: cityOrLga || undefined,
			placeId: `manual:${co.value}:${state || ""}:${cityOrLga || ""}`,
		};

		onChange(result);
		setEntryMode("readonly");
		// Reset manual form fields
		setManualAddressLine("");
		setManualState("");
		setManualCityOrLga("");
	};

	return (
		<FormControl isInvalid={isInvalid}>
			<FormLabel
				fontSize={"md"}
				color="#212121"
				fontWeight="medium"
				className="text-base lg:text-xl"
			>
				Address
			</FormLabel>

			{/* Read-only mode */}
			{entryMode === "readonly" && (
				<InputGroup>
					<Input
						value={value ? value.address : ""}
						isReadOnly
						rounded={0}
						py={{
							base: 5,
							lg: 8,
						}}
						px={{
							base: 3,
							lg: 5,
						}}
						border="1px solid #D7D7D7"
					/>
					<InputRightElement width="4.5rem" height="100%">
						<Button
							h="1.75rem"
							size="sm"
							onClick={() => {
								setEntryMode("search");
								setOpen(true);
							}}
						>
							Edit
						</Button>
					</InputRightElement>
				</InputGroup>
			)}

			{/* Search mode */}
			{entryMode === "search" && (
				<Popover open={open} onOpenChange={setOpen}>
					<PopoverTrigger asChild>
						<div
							role="combobox"
							aria-expanded={open}
							aria-haspopup="listbox"
							aria-controls="address-search-listbox"
							aria-label="Search address"
							tabIndex={0}
							className="w-full border border-[#D7D7D7] px-3 lg:px-5 py-3 lg:py-5 text-left cursor-pointer"
							onClick={() => setOpen(true)}
							onKeyDown={(e) => {
								if (e.key === "Enter" || e.key === " ") {
									e.preventDefault();
									setOpen(true);
								}
							}}
						>
							{value?.address || (
								<span className="text-muted-foreground">
									e.g Plot 15, Ayodele street, Magodo
								</span>
							)}
						</div>
					</PopoverTrigger>
					<PopoverContent
						className="p-0 w-[var(--radix-popover-trigger-width)]"
						align="start"
					>
						<Command filter={() => 1}>
							<CommandInput
								placeholder="Search address..."
								onValueChange={(val) => {
									setSearch(val);
									debouncedFetch(val);
								}}
							/>
							<CommandList id="address-search-listbox" role="listbox">
								{!loading && predictions.length === 0 ? (
									<CommandEmpty>
										{search.trim() ? (
											<div className="flex flex-col items-center gap-3 py-4 px-4">
												<MapPinOff className="h-10 w-10 text-muted-foreground/50" />
												<div className="text-center">
													<p className="font-medium text-foreground">
														No results found
													</p>
													<p className="text-muted-foreground mt-1">
														We couldn't find any addresses matching "
														{search}"
													</p>
												</div>
												<ShadButton
													variant="outline"
													className="mt-2"
													onClick={() => {
														setEntryMode("manual");
														setOpen(false);
													}}
												>
													Enter address manually
												</ShadButton>
											</div>
										) : (
											<span className="text-muted-foreground">
												Start typing to search
											</span>
										)}
									</CommandEmpty>
								) : null}
								{loading && (
									<div className="py-4 px-3 text-sm text-muted-foreground text-center">
										Searching...
									</div>
								)}
								{predictions.map((opt) => (
									<CommandItem
										key={opt.value}
										value={opt.label}
										onSelect={() => {
											handlePredictionClick(opt.value);
											setOpen(false);
										}}
										className="cursor-pointer"
									>
										{opt.label}
									</CommandItem>
								))}
							</CommandList>
						</Command>
					</PopoverContent>
				</Popover>
			)}

			{/* Manual entry mode */}
			{entryMode === "manual" && (
				<div className="space-y-4 border border-[#D7D7D7] p-4">
					{/* Header with back link */}
					<div className="flex items-center justify-between">
						<span className="text-sm font-medium text-[#212121]">
							Enter Address Manually
						</span>
						<button
							type="button"
							className="text-sm text-primary underline hover:no-underline flex items-center gap-1"
							onClick={() => {
								setEntryMode("search");
								setOpen(true);
							}}
						>
							<ArrowLeft className="w-3 h-3" />
							Back to search
						</button>
					</div>

					{/* Country selector - only if country not pre-selected */}
					{!country && (
						<div className="space-y-2">
							<label className="text-sm font-medium">Country</label>
							<select
								className="w-full border border-[#D7D7D7] px-3 py-2"
								value={manualCountry?.value || ""}
								onChange={(e) => {
									const sel = countriesList.find(
										(c) => c.value === e.target.value
									);
									if (sel) {
										setManualCountry(sel);
										onCountryChange?.(sel);
									}
								}}
							>
								<option value="" disabled>
									Select country
								</option>
								{countriesList.map((c) => (
									<option key={c.value} value={c.value}>
										{c.label}
									</option>
								))}
							</select>
						</div>
					)}

					{/* Address line */}
					<div className="space-y-2">
						<label className="text-sm font-medium">Address line</label>
						<Input
							value={manualAddressLine}
							onChange={(e) => setManualAddressLine(e.target.value)}
							placeholder="Street, area or landmark"
							rounded={0}
							border="1px solid #D7D7D7"
							py={{ base: 3, lg: 5 }}
							px={{ base: 3, lg: 5 }}
						/>
					</div>

					{/* Nigeria: LGA Popover */}
					{useNigeriaFlow ? (
						<div className="space-y-2">
							<label className="text-sm font-medium">
								Local Government Area
							</label>
							<Popover open={lgaOpen} onOpenChange={setLgaOpen}>
								<PopoverTrigger asChild>
									<div
										role="combobox"
										aria-expanded={lgaOpen}
										aria-haspopup="listbox"
										aria-controls="lga-search-listbox"
										aria-label="Search Local Government Area"
										tabIndex={0}
										className="w-full border border-[#D7D7D7] px-3 py-2 lg:py-4 cursor-pointer text-left"
										onClick={() => setLgaOpen(true)}
										onKeyDown={(e) => {
											if (e.key === "Enter" || e.key === " ") {
												e.preventDefault();
												setLgaOpen(true);
											}
										}}
									>
										{manualCityOrLga || (
											<span className="text-muted-foreground">
												Search and select an LGA
											</span>
										)}
									</div>
								</PopoverTrigger>
								<PopoverContent
									className="p-0 w-[var(--radix-popover-trigger-width)]"
									align="start"
								>
									<Command>
										<CommandInput placeholder="Search LGA..." />
										<CommandList id="lga-search-listbox" role="listbox">
											<CommandEmpty>No LGAs found</CommandEmpty>
											{allLgas.map((row) => (
												<CommandItem
													key={`${row.state}:${row.lga}`}
													value={`${row.lga} ${row.state}`}
													onSelect={() => {
														setManualCityOrLga(row.lga);
														setManualState(row.state);
														setLgaOpen(false);
													}}
												>
													{row.lga} — {row.state}
												</CommandItem>
											))}
										</CommandList>
									</Command>
								</PopoverContent>
							</Popover>
						</div>
					) : (
						/* Non-Nigeria: State + City inputs */
						<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
							<div className="space-y-2">
								<label className="text-sm font-medium">State/Region</label>
								<Input
									value={manualState}
									onChange={(e) => setManualState(e.target.value)}
									placeholder="e.g. Lagos State"
									rounded={0}
									border="1px solid #D7D7D7"
									py={{ base: 3, lg: 5 }}
									px={{ base: 3, lg: 5 }}
								/>
							</div>
							<div className="space-y-2">
								<label className="text-sm font-medium">City</label>
								<Input
									value={manualCityOrLga}
									onChange={(e) => setManualCityOrLga(e.target.value)}
									placeholder="e.g. Ikeja"
									rounded={0}
									border="1px solid #D7D7D7"
									py={{ base: 3, lg: 5 }}
									px={{ base: 3, lg: 5 }}
								/>
							</div>
						</div>
					)}

					{/* Save button */}
					<div className="flex justify-end">
						<ShadButton
							onClick={saveManualAddress}
							disabled={!selectedCountry || !manualAddressLine}
						>
							Save Address
						</ShadButton>
					</div>
				</div>
			)}

			{locationIsNotDescriptive && (
				<Text color="red.500" fontSize="sm" mt={2}>
					Please select a more descriptive address.
				</Text>
			)}

			{isInvalid && !locationIsNotDescriptive && (
				<FormErrorMessage>Please select an address</FormErrorMessage>
			)}
		</FormControl>
	);
};
